import React, { createContext, useContext, useState, useEffect, useCallback, useReducer } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationHistory, notificationsAPI } from '../../lib/api/notificationApi';

// Firebase imports
import { FirebaseApp, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, Messaging, onMessage } from 'firebase/messaging';
import { firebaseConfig } from '../../lib/firebase/config';

// Initialize Firebase - only if in browser environment
let firebaseApp: FirebaseApp | undefined;
let messaging: Messaging;

// Initialize Firebase only in browser environment
if (typeof window !== 'undefined') {
    try {
        firebaseApp = initializeApp(firebaseConfig);
        isSupported().then(isSupported => {
            if (isSupported) {
                messaging = getMessaging(firebaseApp);
                console.log('Firebase Messaging initialized');
            } else {
                console.log('Firebase Messaging is not supported in this browser');
            }
        });
    } catch (error) {
        console.error('Error initializing Firebase:', error);
    }
}

// Define the notification interface
interface Notification {
    id: string;
    title: string | undefined;
    body: string | undefined;
    data: any;
    isRead?: boolean;
    createdAt?: string;
}

// Define the context interface
interface NotificationContextType {
    notifications: Notification[];
    isPushSupported: boolean;
    isWebPushSupported: boolean;
    requestPushPermission: () => Promise<boolean>;
    requestWebPushPermission: () => Promise<boolean>;
    clearNotifications: () => void;
    pushToken: string | null;
    webPushSubscription: PushSubscription | null;
    hasUnread: boolean;
    unreadCount: number;
    loading: boolean;
    error: string | null;
    markAsRead: (notificationId: string, screen?: string) => Promise<void>;
    refreshNotifications: () => Promise<void>;
    trackNotificationClick: (notificationId: string, screen?: string, action?: string) => Promise<void>;
    trackNotificationDismiss: (notificationId: string, screen?: string) => Promise<void>;
    getNotificationDetails: (notificationId: string) => Promise<any>;
}

// Define action types for reducer
type NotificationAction =
    | { type: 'SET_NOTIFICATIONS'; payload: Notification[] }
    | { type: 'ADD_NOTIFICATION'; payload: Notification }
    | { type: 'MARK_AS_READ'; payload: string }
    | { type: 'CLEAR_NOTIFICATIONS' }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'SET_UNREAD_COUNT'; payload: number }
    | { type: 'SET_PUSH_TOKEN'; payload: string }
    | { type: 'SET_PUSH_SUPPORTED'; payload: boolean }
    | { type: 'SET_WEB_PUSH_SUPPORTED'; payload: boolean }
    | { type: 'SET_WEB_PUSH_SUBSCRIPTION'; payload: PushSubscription | null }
    | { type: 'SET_FCM_TOKEN'; payload: string | null };

// Define state interface
interface NotificationState {
    notifications: Notification[];
    loading: boolean;
    error: string | null;
    unreadCount: number;
    pushToken: string | null;
    isPushSupported: boolean;
    isWebPushSupported: boolean;
    webPushSubscription: PushSubscription | null;
    fcmToken: string | null;
}

// Initial state
const initialState: NotificationState = {
    notifications: [],
    loading: false,
    error: null,
    unreadCount: 0,
    pushToken: null,
    isPushSupported: false,
    isWebPushSupported: false,
    webPushSubscription: null,
    fcmToken: null
};

// VAPID key for web push (still needed for FCM web push)
const VAPID_PUBLIC_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'BMJl_2X_4hgdCJL4djGGdzhpkVM8BlZ-eAMczv2I2Goa2M4kSL1LD-NCNL1c77Ii1tgFXOa8LaJ8FfBGV_fM1Eo';

console.log('VAPID_PUBLIC_KEY:', VAPID_PUBLIC_KEY);

// Reducer function
const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
    switch (action.type) {
        case 'SET_NOTIFICATIONS':
            return {
                ...state,
                notifications: action.payload
            };
        case 'ADD_NOTIFICATION':
            return {
                ...state,
                notifications: [action.payload, ...state.notifications],
                unreadCount: state.unreadCount + 1
            };
        case 'MARK_AS_READ':
            return {
                ...state,
                notifications: state.notifications.map(notification =>
                    notification.id === action.payload
                        ? { ...notification, isRead: true }
                        : notification
                ),
                unreadCount: Math.max(0, state.unreadCount - 1)
            };
        case 'CLEAR_NOTIFICATIONS':
            return {
                ...state,
                notifications: [],
                unreadCount: 0
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            };
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload
            };
        case 'SET_UNREAD_COUNT':
            return {
                ...state,
                unreadCount: action.payload
            };
        case 'SET_PUSH_TOKEN':
            return {
                ...state,
                pushToken: action.payload
            };
        case 'SET_PUSH_SUPPORTED':
            return {
                ...state,
                isPushSupported: action.payload
            };
        case 'SET_WEB_PUSH_SUPPORTED':
            return {
                ...state,
                isWebPushSupported: action.payload
            };
        case 'SET_WEB_PUSH_SUBSCRIPTION':
            return {
                ...state,
                webPushSubscription: action.payload
            };
        case 'SET_FCM_TOKEN':
            return {
                ...state,
                fcmToken: action.payload
            };
        default:
            return state;
    }
};

// Helper function to convert base64 to Uint8Array for web push
function urlB64ToUint8Array(base64String: string): Uint8Array {
    // Padding the base64 string
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    // Decode base64
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    // Convert to Uint8Array
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

// Create the context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Create the provider component
const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Use reducer for related state management
    const [state, dispatch] = useReducer(notificationReducer, initialState);

    // Service worker registration
    const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

    // Get auth context
    const { user } = useAuth();

    // Compute hasUnread from unreadCount (derived state)
    const hasUnread = state.unreadCount > 0;

    // Effect for checking push support - should only run once
    useEffect(() => {
        // Check if running on a native platform
        const isNative = Capacitor.isNativePlatform();
        dispatch({ type: 'SET_PUSH_SUPPORTED', payload: isNative });

        // Check if web push is supported
        const isWebPushSupported = 'serviceWorker' in navigator && 'PushManager' in window;
        console.log('Web Push supported:', isWebPushSupported);
        dispatch({ type: 'SET_WEB_PUSH_SUPPORTED', payload: isWebPushSupported });

        // Register service worker for web push if supported
        if (isWebPushSupported) {
            registerServiceWorker();
        }
    }, []);

    // Register service worker for web push
    const registerServiceWorker = async () => {
        try {
            const swPath = '/app/firebase-messaging-sw.js'; // Corregir ruta
            console.log(`Registering service worker at: ${swPath}`);

            const registration = await navigator.serviceWorker.register(swPath);
            setSwRegistration(registration);
            console.log('Service Worker registered successfully:', registration);

            // Solicitar permiso inmediatamente, sin importar si el usuario está autenticado
            requestNotificationPermissionImmediately();
        } catch (error) {
            console.error('Error registering Service Worker:', error);
        }
    };

    useEffect(() => {
        if (!user) return;

        const initializeNotifications = async () => {
            refreshNotifications();

            if (state.isPushSupported) {
                initializePushNotifications();
            }
            console.log('isWebPushSupported:', state.isWebPushSupported);
            if (state.isWebPushSupported && swRegistration && !state.fcmToken) {
                requestWebPushPermission();
            }
        };

        initializeNotifications();

        return () => {
            if (state.isPushSupported) {
                PushNotifications.removeAllListeners();
            }
        };
    }, [user, state.isPushSupported, state.isWebPushSupported, swRegistration]);

    // Initialize push notifications for native platforms
    const initializePushNotifications = useCallback(async () => {
        try {
            await requestPushPermission();
        } catch (error) {
            console.error('Error initializing push notifications:', error);
        }
    }, []);

    // Request push permission for native apps
    const requestPushPermission = useCallback(async (): Promise<boolean> => {
        try {
            // Request permission to use push notifications
            const result = await PushNotifications.requestPermissions();

            if (result.receive === 'granted') {
                // Register with Apple / Google to receive push
                await PushNotifications.register();

                // Add listeners
                addPushListeners();

                return true;
            } else {
                console.log('Push notification permission denied');
                return false;
            }
        } catch (error) {
            console.error('Error requesting push permissions:', error);
            return false;
        }
    }, []);

    // Request web push permission for browsers using Firebase
    const requestWebPushPermission = useCallback(async (): Promise<boolean> => {
        if (!state.isWebPushSupported || !messaging) {
            console.log('Web Push not supported or Firebase messaging not initialized');
            return false;
        }

        try {
            console.log('Requesting permission for web push notifications...');

            // Request notification permission
            const permission = await Notification.requestPermission();
            console.log('Notification permission:', permission);

            if (permission === 'granted') {
                // Get FCM token with the VAPID key
                console.log('Using VAPID key:', VAPID_PUBLIC_KEY);

                if (!swRegistration) {
                    console.error('Service Worker registration not found');
                    return false;
                }

                const fcmToken = await getToken(messaging, {
                    vapidKey: VAPID_PUBLIC_KEY,
                    serviceWorkerRegistration: swRegistration
                });

                if (!fcmToken) {
                    console.error('No FCM token received');
                    return false;
                }

                console.log('FCM token received:', fcmToken);

                // Save FCM token in state
                dispatch({ type: 'SET_FCM_TOKEN', payload: fcmToken });

                // Create a subscription object compatible with our backend
                // This maintains compatibility with the existing web-push backend
                const subscription = {
                    endpoint: `https://fcm.googleapis.com/fcm/send/${fcmToken}`,
                    keys: {
                        p256dh: 'FCM-COMPATIBLE',
                        auth: 'FCM-COMPATIBLE'
                    },
                };

                // Register with backend
                await registerWebPushSubscriptionWithBackend(subscription, fcmToken);

                // Setup foreground message handler
                setupForegroundMessageHandler();

                return true;
            } else {
                console.log('Web Push permission denied');
                return false;
            }
        } catch (error) {
            console.error('Error subscribing to web push:', error);
            return false;
        }
    }, [state.isWebPushSupported]);

    const requestNotificationPermissionImmediately = useCallback(async () => {
        if (state.isWebPushSupported && messaging) {
            console.log('Solicitando permiso inmediatamente...');
            try {
                await requestWebPushPermission();
            } catch (error) {
                console.error('Error solicitando permiso inicial:', error);
            }
        }
    }, [state.isWebPushSupported, requestWebPushPermission]);

    // Setup handler for foreground messages
    const setupForegroundMessageHandler = useCallback(() => {
        if (!messaging) return;

        // Handle foreground messages
        const unsubscribe = onMessage(messaging, (payload) => {
            console.log('Foreground message received:', payload);

            // Convert to our notification format
            const notification = {
                id: `fcm-${new Date().getTime()}`,
                title: payload.notification?.title,
                body: payload.notification?.body,
                data: payload.data || {},
                isRead: false,
                createdAt: new Date().toISOString()
            };

            // Add to local notifications
            addNotification(notification);

            // Refresh notifications from server
            refreshNotifications();
        });

        // Return unsubscribe function for cleanup
        return unsubscribe;
    }, []);

    // Register web push subscription with backend
    const registerWebPushSubscriptionWithBackend = useCallback(async (subscription: any, fcmToken: any) => {
        if (!user) return;

        try {
            // Detect browser
            const userAgent = navigator.userAgent.toLowerCase();
            let browser = 'other';

            if (userAgent.indexOf('firefox') !== -1) {
                browser = 'firefox';
            } else if (userAgent.indexOf('chrome') !== -1) {
                browser = 'chrome';
            } else if (userAgent.indexOf('safari') !== -1) {
                browser = 'safari';
            } else if (userAgent.indexOf('edge') !== -1) {
                browser = 'edge';
            }

            // Get device info
            const deviceInfo = {
                type: window.innerWidth <= 768 ? 'mobile' : 'desktop',
                platform: 'web',
                userAgent: navigator.userAgent,
                language: navigator.language
            };

            console.log('Registering web push subscription with backend...');

            // Convert subscription to string if it's an object
            const subscriptionString = typeof subscription === 'string'
                ? subscription
                : JSON.stringify(subscription);

            // Send subscription to backend
            const response = await notificationsAPI.registerWebPushSubscription(
                subscriptionString,
                browser,
                deviceInfo,
                fcmToken
            );

            console.log('Web push subscription registered with backend:', response);
        } catch (error) {
            console.error('Error registering web push subscription:', error);
        }
    }, [user]);

    // Add push notification listeners for native platforms
    const addPushListeners = useCallback(() => {
        // Registration success
        PushNotifications.addListener('registration', token => {
            console.log('Push registration success:', token.value);
            dispatch({ type: 'SET_PUSH_TOKEN', payload: token.value });

            // Register token with backend
            registerTokenWithBackend(token.value);
        });

        // Registration error
        PushNotifications.addListener('registrationError', err => {
            console.error('Push registration failed:', err);
        });

        // Push received while app is in foreground
        PushNotifications.addListener('pushNotificationReceived', notification => {
            console.log('Push notification received:', notification);

            // Add to local notifications
            addNotification(notification);

            // Refresh notifications
            refreshNotifications();
        });

        // Push action performed (notification clicked)
        PushNotifications.addListener('pushNotificationActionPerformed', notification => {
            console.log('Push notification action performed:', notification);

            // Handle navigation if notification has route data
            handleNotificationNavigation(notification);

            // Refresh notifications
            refreshNotifications();
        });
    }, []);

    // Register token with backend
    const registerTokenWithBackend = useCallback(async (token: string) => {
        try {
            if (!user) return;

            const response = await notificationsAPI.registerToken(token);
            console.log('Token registered with backend:', response);
        } catch (error) {
            console.error('Error registering token with backend:', error);
        }
    }, [user]);

    // Add notification to local state
    const addNotification = useCallback((notification: any) => {
        const newNotification: Notification = {
            id: notification._id || `local-${new Date().getTime()}`,
            title: notification.title,
            body: notification.body,
            data: notification.data,
            isRead: false,
            createdAt: new Date().toISOString()
        };

        dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
    }, []);

    // Handle notification navigation
    const handleNotificationNavigation = useCallback((notification: any) => {
        // Check if notification has route data
        const data = notification.notification?.data;
        if (data && data.route) {
            // TODO: Implement navigation to the specified route
            console.log('Should navigate to:', data.route);
        }
    }, []);

    // Refresh notifications from server
    const refreshNotifications = useCallback(async () => {
        if (!user) return;

        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        try {
            const response = await notificationsAPI.getHistory();

            const isNotificationRead = (notification: NotificationHistory): boolean => {
                if (!notification.analytics?.openedBy || notification.analytics.openedBy.length === 0) {
                    return false;
                }

                const isRead = notification.analytics.openedBy.some(
                    record => record.userId === user.firebaseId
                );

                return isRead;
            };

            // Convert server notifications to our format
            const formattedNotifications = (response.notifications || response.data || []).map((notification: any) => ({
                id: notification._id,
                title: notification.title,
                body: notification.body,
                data: notification.data,
                isRead: isNotificationRead(notification),
                createdAt: notification.createdAt || notification.sentAt
            }));

            dispatch({ type: 'SET_NOTIFICATIONS', payload: formattedNotifications });

            // Count unread notifications
            const unreadCount = formattedNotifications.filter((n: Notification) => !n.isRead).length;
            dispatch({ type: 'SET_UNREAD_COUNT', payload: unreadCount });
        } catch (error) {
            console.error('Error loading notifications:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Failed to load notifications' });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [user]);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId: string, screen: string = 'notification-list') => {
        if (!user) return;

        try {
            await notificationsAPI.markAsRead(notificationId, screen);
            dispatch({ type: 'MARK_AS_READ', payload: notificationId });
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, [user]);

    // Track notification click
    const trackNotificationClick = useCallback(async (
        notificationId: string,
        screen: string = 'notification-list',
        action: string = 'default'
    ) => {
        if (!user) return;

        try {
            await notificationsAPI.trackNotificationClick(notificationId, screen, action);

            // Find notification to get route data
            const notification = state.notifications.find(n => n.id === notificationId);
            if (notification?.data?.route) {
                // Handle navigation
                console.log('Navigating to:', notification.data.route);
            }

            // Mark as read after clicking
            dispatch({ type: 'MARK_AS_READ', payload: notificationId });
        } catch (error) {
            console.error('Error tracking notification click:', error);
        }
    }, [user, state.notifications]);

    // Track notification dismiss
    const trackNotificationDismiss = useCallback(async (notificationId: string, screen: string = 'notification-list') => {
        if (!user) return;

        try {
            await notificationsAPI.trackNotificationDismiss(notificationId, screen);
        } catch (error) {
            console.error('Error tracking notification dismiss:', error);
        }
    }, [user]);

    // Get notification details
    const getNotificationDetails = useCallback(async (notificationId: string) => {
        if (!user) return null;

        try {
            return await notificationsAPI.getNotificationAnalytics(notificationId);
        } catch (error) {
            console.error('Error getting notification details:', error);
            return null;
        }
    }, [user]);

    // Clear all notifications
    const clearNotifications = useCallback(() => {
        dispatch({ type: 'CLEAR_NOTIFICATIONS' });
    }, []);

    // Listen for service worker messages
    useEffect(() => {
        if (!navigator.serviceWorker) return;

        const handleServiceWorkerMessage = (event: MessageEvent) => {
            if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
                console.log('Message received from service worker:', event.data);

                // Handle navigation or any necessary action
                if (event.data.route) {
                    console.log('Navigating to route from service worker:', event.data.route);
                    // Implement navigation
                }

                // Track notification click if ID is provided
                if (event.data.notificationId) {
                    trackNotificationClick(
                        event.data.notificationId,
                        'service-worker',
                        event.data.action || 'click'
                    );
                }
            }
        };

        // Add listener for service worker messages
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

        // Clean up when unmounting
        return () => {
            navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
        };
    }, [trackNotificationClick]);

    // Create context value - memoized to prevent unnecessary re-renders
    const contextValue = React.useMemo(() => ({
        notifications: state.notifications,
        isPushSupported: state.isPushSupported,
        isWebPushSupported: state.isWebPushSupported,
        requestPushPermission,
        requestWebPushPermission,
        clearNotifications,
        pushToken: state.pushToken,
        webPushSubscription: state.webPushSubscription,
        hasUnread,
        unreadCount: state.unreadCount,
        loading: state.loading,
        error: state.error,
        markAsRead,
        refreshNotifications,
        trackNotificationClick,
        trackNotificationDismiss,
        getNotificationDetails
    }), [
        state,
        hasUnread,
        requestPushPermission,
        requestWebPushPermission,
        clearNotifications,
        markAsRead,
        refreshNotifications,
        trackNotificationClick,
        trackNotificationDismiss,
        getNotificationDetails
    ]);

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
        </NotificationContext.Provider>
    );
};

// Custom hook to use the notification context
const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export { NotificationProvider, useNotifications };